import { useDebouncedValue } from '@tanstack/react-pacer'
import { useEffect, useMemo, useState } from 'react'
import {
  useIntegrationInstanceCount,
  useIntegrationInstances,
  useIntegrationTypesWithInstances,
} from '../../utils/api-client/api-hooks.ts'
import type { Integration } from '../../utils/api-client/api-schemas.ts'
import { SEARCH_DEBOUNCE_WAIT_MS, useIsStalePlaceholderData } from '../../utils/query-utils.ts'
import {
  convertIntegrationTypeToTool,
  getIntegrationTypeName,
  getIsToolIntegrationType,
  isToolIntegration,
  type ToolIntegration,
} from '../../utils/repository-utils.tsx'
import { ToolIcon } from '../third-party-branding/tool-icon.tsx'
import { SearchableCombobox } from './searchable-combobox.tsx'
import { SelectBox } from './select-box.tsx'

export type ToolIntegrationType = ToolIntegration['type']

export type IntegrationInstanceSelection = {
  type?: ToolIntegrationType
  integrationId?: string
  // True when the chosen instance is configured in the admin console. The platform resolves a supplied
  // instance id against managed instances only, so a host must omit the id to choose this one.
  isSystemInstance?: boolean
}

export type IntegrationInstanceSelectorProps = {
  value: IntegrationInstanceSelection
  onChange: (value: IntegrationInstanceSelection) => unknown
  /** Fixes the tool, hiding the tool control — for surfaces already scoped to one tool. */
  fixedType?: ToolIntegrationType
  /** Restricts which tools the tool control offers. Defaults to every tool with an instance. */
  typeFilter?: readonly ToolIntegrationType[]
  toolLabel?: string
  instanceLabel?: string
  /**
   * Marks the instance field required. Set by surfaces that refuse to persist an unattributed
   * record; the surface still owns the submit gate, this only makes the requirement visible. Ignored
   * while the selected tool has no instances, since there is nothing to pick.
   */
  isInstanceRequired?: boolean
  /** Shown as the instance field's helper text when the selected tool has no instances connected. */
  emptyStateText?: string
}

/**
 * Instances of the same tool share an icon and often a name too, so the host is what tells them
 * apart. It is passed as option metadata rather than put in the label, which keeps it out of the
 * value the trigger displays and out of the text the search highlights.
 *
 * Shown, but not searchable for every instance: the platform matches `search` against a managed
 * instance's base URI, and against a system instance's id and name only
 * (`ManagedIntegrationQueries.filter` and `RegistryIntegrationsResource.matchesTerm`). So typing a
 * host narrows the managed half and returns nothing for two system instances that differ only in
 * their host — for those the host is there to read, and the id or the derived name is what a search
 * matches.
 */
const getInstanceHost = (integration: ToolIntegration): string | undefined => {
  const baseUri = integration.base_uri
  if (!baseUri) {
    return undefined
  }
  try {
    return new URL(baseUri).host
  } catch {
    return baseUri
  }
}

type InstanceOption = { id: string; label: string; metadata?: string; integration: ToolIntegration }

/**
 * The instances of the given tool, out of one page of the collection.
 *
 * The `type` test is not redundant with the server-side `type` param: `keepPreviousData` holds the
 * previous tool's page on screen while the new tool's request is in flight, so between
 * `handleTypeChange` and that response `integrations` is the old tool's instances. Filtering here
 * empties the list instead of offering instances of the tool the user just navigated away from.
 */
const getInstancesForType = (integrations: Integration[], type: ToolIntegrationType | undefined): ToolIntegration[] =>
  integrations.filter(isToolIntegration).filter(integration => integration.type === type)

/**
 * The label names the instance and nothing else.
 *
 * Every option is an instance of the tool already chosen — the request is narrowed by `type` and
 * `getInstancesForType` drops the rest — and each row draws that tool's icon, so a `Datadog — ` on
 * every row repeats what the Tool control above already says.
 *
 * It is also what keeps the label typeable. The field doubles as the search box and forwards its
 * text to the platform, which matches an instance's name, base URI and id — never a composed
 * `Datadog — Production`. While the label was composed, editing a settled selection handed that
 * whole string to `search` and emptied the list under the user, and typing what was on screen from
 * an empty field did the same.
 *
 * An instance with no name of its own falls back to the tool's name, which is how a system singleton
 * already reads ("Datadog"); the host on the second line is what separates two of those. The host is
 * shown whenever the instance has one, rather than only when two options on screen share a label:
 * the options are one server-narrowed page, so an identically-named sibling may exist and simply not
 * be on it, and hiding the host then would drop the only thing distinguishing them exactly when a
 * user is searching by it.
 */
const buildInstanceOptions = (integrations: ToolIntegration[]): InstanceOption[] =>
  integrations.map(integration => ({
    id: integration.id,
    label: integration.name || getIntegrationTypeName(integration.type),
    metadata: getInstanceHost(integration),
    integration,
  }))

export const IntegrationInstanceSelector: React.FC<IntegrationInstanceSelectorProps> = ({
  value,
  onChange,
  fixedType,
  typeFilter,
  toolLabel = 'Tool',
  instanceLabel = 'Instance',
  isInstanceRequired = false,
  emptyStateText = 'No instances of this tool are connected yet. Connect one in Organization → Integrations.',
}) => {
  const selectedType = fixedType ?? value.type
  // Undefined means "showing the selected instance's label" rather than a query the user typed.
  const [instanceQuery, setInstanceQuery] = useState<string | undefined>(undefined)

  // Debounced so a request is issued per pause rather than per keystroke. Debouncing alone says
  // nothing about what happens to the data on the old key, which is what `keepPreviousData` below is
  // for.
  const [debouncedInstanceQuery] = useDebouncedValue(instanceQuery ?? '', { wait: SEARCH_DEBOUNCE_WAIT_MS })

  // The tool control offers only tools that have an instance, which is a question about the whole
  // collection rather than a page of it — so it reads the counts endpoint instead of paging the
  // collection to derive the same answer.
  const {
    types: typesWithInstances,
    isLoading: isLoadingTypeCounts,
    isError: isTypeCountsError,
  } = useIntegrationTypesWithInstances()

  const availableTypes = useMemo(() => {
    const types = typesWithInstances.filter(getIsToolIntegrationType)
    return types.filter(type => !typeFilter || typeFilter.includes(type)).sort()
  }, [typesWithInstances, typeFilter])

  // One page of the instances of the selected tool, narrowed by the typed term server-side.
  // `useIntegrationInstances` owns the page size, skips the request until a tool is chosen, and
  // keeps the previous page on screen while the next one is in flight.
  const {
    data: instancePage,
    isLoading: isLoadingInstances,
    isPlaceholderData: isShowingPreviousPage,
  } = useIntegrationInstances({ type: selectedType, search: debouncedInstanceQuery })

  // The previous page stays on screen while the next one is in flight, so the spinner is what tells
  // the user the list is behind their typing — delayed, so a fast narrowing does not flash it.
  const isShowingStalePage = useIsStalePlaceholderData(isShowingPreviousPage)
  const isLoading = isLoadingTypeCounts || (selectedType !== undefined && isLoadingInstances)

  const instancesForType = useMemo(
    () => getInstancesForType(instancePage?._embedded?.items ?? [], selectedType),
    [instancePage, selectedType]
  )

  /**
   * How many instances the selected tool has in total, ignoring whatever the user has typed.
   *
   * Read from the counts rather than from the page, because the page is narrowed by the search term.
   * Two decisions below depend on the total and would be wrong if taken from the narrowed list: a
   * field that disables itself at one option would disable mid-typing the moment a query matched one
   * instance, and an empty-state message would claim the tool has no instances when a search simply
   * matched none.
   */
  const { count: instanceCountForType } = useIntegrationInstanceCount(selectedType)

  const instanceOptions = useMemo(() => buildInstanceOptions(instancesForType), [instancesForType])

  /**
   * The instance the user picked, kept by id so the field can name it once it is off the page.
   *
   * `instanceOptions` is one server-narrowed page, and selecting an instance clears the search term
   * — so the page that replaces the narrowed one is the tool's unnarrowed first page, which past
   * `PICKER_PAGE_SIZE` instances need not list the instance just chosen. Reading the label
   * from the page alone blanked the field there while the form still held the selection.
   *
   * Keyed by id rather than kept as a bare string because it must not outlive the selection it
   * describes: both hosts clear `integrationId` when the tool changes, and an id that no longer
   * matches falls back to the page lookup.
   */
  const [pickedInstance, setPickedInstance] = useState<{ id: string; label: string } | undefined>(undefined)

  const selectedLabel =
    instanceOptions.find(option => option.id === value.integrationId)?.label ??
    (pickedInstance && pickedInstance.id === value.integrationId ? pickedInstance.label : undefined)

  // Default-to-one: a tool with a single instance needs no choice, so select it up front. Gated on
  // the tool's total as well as on the page, so a search that happens to match one instance out of
  // many does not silently select it.
  useEffect(() => {
    if (
      !selectedType ||
      value.integrationId !== undefined ||
      instanceCountForType !== 1 ||
      instancesForType.length !== 1
    ) {
      return
    }
    const [only] = instancesForType
    if (only) {
      onChange({ type: selectedType, integrationId: only.id, isSystemInstance: only.source === 'system' })
    }
  }, [selectedType, value.integrationId, instanceCountForType, instancesForType, onChange])

  const handleTypeChange = (nextType: string) => {
    const type = availableTypes.find(candidate => candidate === nextType)
    setInstanceQuery(undefined)
    onChange({ type, integrationId: undefined })
  }

  /**
   * The combobox writes the chosen option's value — an instance id — into its field. Selecting is
   * therefore an id arriving here, which we report upwards and then display as that instance's
   * label; anything else is free-typed search text.
   */
  const handleInstanceSearchChange = (nextValue: string) => {
    const matched = instanceOptions.find(option => option.id === nextValue)
    if (matched) {
      setInstanceQuery(undefined)
      setPickedInstance({ id: matched.id, label: matched.label })
      onChange({
        type: selectedType,
        integrationId: matched.id,
        isSystemInstance: matched.integration.source === 'system',
      })
      return
    }
    setInstanceQuery(nextValue)
  }

  // Keyed off `isLoading`, not `isFetching`: the latter goes true again on every background refetch
  // and would blink the message away and back. Read from the tool's total rather than the current
  // page, so a search matching nothing does not report the tool as having no instances at all.
  const hasNoInstances = selectedType !== undefined && !isLoading && instanceCountForType === 0
  // One instance is not a choice: it is already selected above, so the field states it and stops
  // behaving like a control. Mirrors the Integration field in the New repository modal.
  const hasOnlyOneInstance = instanceCountForType === 1

  return (
    <>
      {fixedType === undefined && (
        <SelectBox
          label={toolLabel}
          placeholder="Select a tool"
          value={value.type}
          onValueChange={handleTypeChange}
          // An errored counts request also produces no types, and this control is the only thing
          // that says which tools exist — so it says the request failed rather than letting an
          // empty list read as "your org has connected nothing".
          errorText={isTypeCountsError ? 'Could not load tools. Reload the page to try again.' : undefined}
          options={availableTypes.map(type => ({
            value: type,
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ToolIcon tool={convertIntegrationTypeToTool(type)} size="small" />
                {getIntegrationTypeName(type)}
              </span>
            ),
          }))}
        />
      )}
      {selectedType !== undefined && (
        <SearchableCombobox
          label={instanceLabel}
          isRequired={isInstanceRequired && !hasNoInstances}
          placeholder="Search instances..."
          // Ariakit treats an undefined value as uncontrolled and keeps its own last value, which
          // would strand the previous tool's instance in the field after switching tools.
          searchValue={instanceQuery ?? selectedLabel ?? ''}
          setSearchValue={handleInstanceSearchChange}
          isFetching={isLoading || isShowingStalePage}
          // This is a typeahead, so clearing the query is useful; the control only shows while the
          // field has focus, so a settled selection is not left wearing a ✕.
          isDisabled={hasOnlyOneInstance}
          helperText={hasNoInstances ? emptyStateText : undefined}
          options={instanceOptions.map(option => ({
            value: option.id,
            label: option.label,
            metadata: option.metadata,
            checked: option.id === value.integrationId,
            icon: <ToolIcon tool={convertIntegrationTypeToTool(option.integration.type)} size="medium" />,
          }))}
        />
      )}
    </>
  )
}
